/**
 * Which emoji have an animation (animated reactions, phase 1).
 *
 * Phase 1 animates the eight quick reactions only (QUICK_REACTIONS in
 * CommentReactions.tsx). Every other emoji, and any odd text the database
 * stores, has no entry here and stays plain text exactly as before.
 *
 * The files are Google's Noto Animated Emoji, unmodified Lottie JSON, kept in
 * src/assets/emoji/ (credit and license in the README there). Each one is a
 * dynamic import, so Vite hashes it into its own file under /assets/ (the
 * long cache rule in public/_headers) and the browser only downloads it the
 * first time that emoji needs to move. Nothing is fetched from Google at run
 * time.
 */

/** Shown as the title of the quick reaction row. Required by CC BY 4.0. */
export const NOTO_CREDIT =
  'Animated emoji: Noto Animated Emoji by Google (https://googlefonts.github.io/noto-emoji-animation/), licensed CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/). Unmodified Lottie files.';

/** A Lottie file, as far as this app reads it. */
export interface LottieData {
  markers?: { cm?: string; tm?: number }[];
}

type Loader = () => Promise<{ default: unknown }>;

/** Character to loader. Keys are the exact strings in QUICK_REACTIONS. */
const NOTO_FILES: ReadonlyMap<string, Loader> = new Map<string, Loader>([
  ['👍', () => import('../../assets/emoji/1f44d.json')],
  ['❤️', () => import('../../assets/emoji/2764_fe0f.json')],
  ['😂', () => import('../../assets/emoji/1f602.json')],
  ['🎉', () => import('../../assets/emoji/1f389.json')],
  ['👀', () => import('../../assets/emoji/1f440.json')],
  ['🙏', () => import('../../assets/emoji/1f64f.json')],
  ['✅', () => import('../../assets/emoji/2705.json')],
  ['🔥', () => import('../../assets/emoji/1f525.json')],
]);

export function hasNotoAnimation(emoji: string): boolean {
  return NOTO_FILES.has(emoji);
}

/** Every character that has a file, for tests. */
export function notoAnimatedEmoji(): string[] {
  return [...NOTO_FILES.keys()];
}

export async function loadNotoAnimation(emoji: string): Promise<LottieData | null> {
  const load = NOTO_FILES.get(emoji);
  if (load === undefined) {
    return null;
  }
  const mod = await load();
  return mod.default as LottieData;
}

/**
 * The still frame. Each Noto file marks it with a "rest" marker (the Noto
 * site says so, and seven of the eight have one); a file without one rests on
 * its first frame.
 */
export function restFrameOf(data: LottieData): number {
  const rest = data.markers?.find((marker) => marker.cm === 'rest');
  return typeof rest?.tm === 'number' ? rest.tm : 0;
}
